import spacetime from 'spacetime';
import cn from 'classnames';
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { DatePicker } from 'UIPackage/MainModule/components/DatePicker';
import { controlOfNewsLetterTableColumns } from 'APIPackage/ControlIfNewsletterModule/ControlOfNewsLetterTableColumns';
import { ISmevResend } from 'APIPackage/ControlIfNewsletterModule/interfaces/ISmevResend';
import { ROUTE_KNM } from 'CorePackage/MainModule/routes/routes';

import { adminGetSmevResendBySendingDate, adminGetSmevResendBySendingDateNextPage } from '../actions/adminAction';
import {
  getAdminControlOfNewslettersList,
  getAdminControlOfNewslettersListPage,
  getAdminControlOfNewslettersTotalCount,
} from '../selectors/adminSelectors';
import styles from './NewsTable.module.scss';
import tableStyles from './ControlOfNewsLettesPage.module.scss';

interface IControlOfNewsLettersPageStoreProps {
  loading: boolean;
  controlOfNewslettersList: ISmevResend[];
  totalCount: number;
  page: number;
}

export const ControlOfNewsLettersPageWithoutStore = ({
  loading,
  controlOfNewslettersList,
  totalCount,
  page,
}: IControlOfNewsLettersPageStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [dateStart, setDateStart] = useState<Date>(spacetime().add(-5, 'day').toNativeDate());
  const [dateEnd, setDateEnd] = useState<Date>(spacetime().add(5, 'day').toNativeDate());
  const tbodyRowClassName = cn(styles.Row, styles.TbodyRow);
  const theadDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, styles.TheadCell);
  const tbodyDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, styles.TbodyCell);
  const tbodyReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, styles.TbodyCell);

  useEffect(() => {
    dispatch(adminGetSmevResendBySendingDate(dateStart, dateEnd));
  }, [dispatch]);

  const onLoadMore = useCallback(() => dispatch(adminGetSmevResendBySendingDateNextPage(dateStart, dateEnd)), [
    dispatch,
    dateStart,
    dateEnd,
  ]);

  const table = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (page <= 0) {
      return;
    }
    setTimeout(() => {
      table.current?.scrollIntoView(false);
    }, 0);
  }, [page]);

  useEffect(() => {
    dispatch(adminGetSmevResendBySendingDate(dateStart, dateEnd));
  }, [dispatch]);

  const onGetNewListByNewDates = useCallback(() => {
    dispatch(adminGetSmevResendBySendingDate(dateStart, dateEnd));
  }, [dispatch, dateStart, dateEnd]);

  /**
   * Отрисовка даты в колонке
   */
  const renderDate = useCallback((date: string | undefined, dateFormat: string) => {
    if (!date) {
      return '';
    }

    return formatDate({
      date,
      dateFormat,
      parseFormat: DATE_FORMAT_ISO_FNS,
    });
  }, []);

  /**
   * Отрисовка строки таблицы
   */
  const renderRow = useCallback((row: ISmevResend, key: number) => {
    return (
      <tr className={tbodyRowClassName} key={key}>
        <td style={{ flex: '1 1 20%' }} className={`${tbodyDateCellClassName} ${tableStyles.KnmLink}`}>
          <a href={ROUTE_KNM(row.knmId)} target="_blank" rel="noreferrer">
            {row.erpId}
          </a>
        </td>
        <td style={{ flex: '1 1 12%' }} className={tbodyDateCellClassName}>
          {renderDate(row.creationDate, DATE_FORMAT_LONG_FNS)}
        </td>
        <td style={{ flex: '1 1 17%' }} className={tbodyDateCellClassName}>
          {renderDate(row.sendingDate, DATE_FORMAT_LONG_MINUTE_FNS)}
        </td>
        <td style={{ flex: '1 1 20%' }} className={tbodyDateCellClassName}>
          {renderDate(row.lastAttemptDate, DATE_FORMAT_LONG_MINUTE_FNS)}
        </td>
        <td style={{ flex: '1 1 23%' }} className={tbodyReasonCellClassName}>
          {row.timeOfDelay}
        </td>
        <td style={{ flex: '1 1 14%' }} className={tbodyReasonCellClassName}>
          {row.attemptCount}
        </td>
      </tr>
    );
  }, []);

  return (
    <div className={tableStyles.TableWrapper}>
      <div className={tableStyles.Header}>
        <h1 className={styles.Title}>Контроль рассылок</h1>
        <DatePicker
          className={`shared-row-field ${tableStyles.DatePicker} ${!loading && tableStyles.DatePickerZPos}`}
          id="date1"
          name="date1"
          value={formatDate({
            date: dateStart,
            dateFormat: DATE_FORMAT_LONG_FNS,
          })}
          onChange={val =>
            setDateStart(
              spacetime(
                formatDate({ date: val, dateFormat: DATE_FORMAT_ISO_FNS, parseFormat: DATE_FORMAT_LONG_FNS }),
              ).toNativeDate(),
            )
          }
        />
        <DatePicker
          className={`shared-row-field ${tableStyles.DatePicker} ${!loading && tableStyles.DatePickerZPos}`}
          id="date2"
          name="date2"
          value={formatDate({
            date: dateEnd,
            dateFormat: DATE_FORMAT_LONG_FNS,
          })}
          maxDate={!dateStart ? undefined : spacetime(dateStart).add(1, 'year').toNativeDate()}
          onChange={val =>
            setDateEnd(
              spacetime(
                formatDate({ date: val, dateFormat: DATE_FORMAT_ISO_FNS, parseFormat: DATE_FORMAT_LONG_FNS }),
              ).toNativeDate(),
            )
          }
        />
        <Button
          className={tableStyles.FormButton}
          onClick={onGetNewListByNewDates}
          size="medium"
          color="primary"
          text="Показать"
          id="addButton"
        />
      </div>
      <table className={tableStyles.Table} ref={table}>
        <thead className={tableStyles.TableHead}>
          <tr className={styles.Row}>
            {controlOfNewsLetterTableColumns.map((row, i) => (
              <th data-id={row.id} key={i} className={theadDateCellClassName} style={row.style}>
                {row.name}
              </th>
            ))}
          </tr>
        </thead>
        {!loading && <tbody>{controlOfNewslettersList.map(renderRow)}</tbody>}
      </table>
      {controlOfNewslettersList.length < totalCount && (
        <Button type="button" text="Показать еще" color="load" onClick={onLoadMore} />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IControlOfNewsLettersPageStoreProps => ({
  controlOfNewslettersList: getAdminControlOfNewslettersList(state),
  page: getAdminControlOfNewslettersListPage(state),
  totalCount: getAdminControlOfNewslettersTotalCount(state),
  loading: getCoreIsLoading(state),
});

export const ControlOfNewsLettersPage = connect(mapStateToProps)(ControlOfNewsLettersPageWithoutStore);
